import axios from 'axios';
import MockAdapter from 'axios-mock-adapter';
import qs from 'query-string';
import store from 'store';
import { resetUserState } from 'actions/index';

export const mockAPI =
  process.env.NODE_ENV === 'test' || process.env.STORYBOOK_GIT_BRANCH
    ? new MockAdapter(axios, { delayResponse: 100 })
    : null;

// Global handle HTTP 401 (Unauthorized)
axios.interceptors.response.use(undefined, (error) => {
  if (error.response && error.response.status === 401) {
    store.dispatch(resetUserState());
  }
  // eslint-disable-next-line prefer-promise-reject-errors
  return Promise.reject({ timestamp: +new Date(), ...error.response });
});

/**
 * User
 */
const fetchUserSelf = () => axios.get('/api/user').then((res) => res.data.results);

const fetchTodos = () => axios.get('/api/user/todo').then((r) => r.data);

const fetchSelfEvents = () => axios.get('/api/user/events').then((res) => res.data);

const updateSelfEvents = (body) => axios.post('/api/user/events', body).then((res) => res.data);

const deleteUser = () => axios.delete('/api/user').then((res) => res.data);

const fetchBankIdToken = () => {
  return axios.get('/api/auth/bankid/token').then((res) => res.data);
};

const bankIdLogin = (token) => {
  return axios.post('/api/auth/bankid/login', { token }).then((res) => res.data);
};

/**
 * Cottages
 */
const fetchCottages = () => axios.get('/api/cottages').then((res) => res.data);

const fetchRentableCottages = () => axios.get('/api/cottages/rentable').then((r) => r.data);

const fetchCottage = (cottageId) => axios.get(`/api/cottages/${cottageId}`).then((res) => res.data);

const updateCottage = (cottageId, payload) =>
  axios.put(`/api/cottages/${cottageId}`, payload).then((res) => res.data);

const postCottage = (data) => axios.post('/api/cottages', data).then((res) => res.data);

const verifyCottage = (cottageId) => {
  return axios.post(`/api/cottages/${cottageId}/verify`).then((res) => res.data);
};

const removeCottage = (cottageId) =>
  axios.delete(`/api/cottages/${cottageId}`).then((res) => res.data);

const getCottageSignedUrl = (cottageId, file, callback) => {
  const params = {
    fileName: file.name.toLowerCase(),
  };

  return axios
    .post(`/api/cottages/${cottageId}/images/upload`, params)
    .then((res) => callback(res.data.results));
};

const fetchStats = (cottageId) =>
  axios.get(`/api/cottages/${cottageId}/stats`).then((res) => res.data);

const updateUserSettings = (userId, payload) =>
  axios.put('/api/user?authenticate=true', payload).then((res) => res.data);

const updateUserHostInformation = (data) => {
  return axios.put('/api/user/host-information', data).then((r) => r.data);
};

const scaEnroll = (data) => {
  return axios.post('/api/user/sca-enroll', data).then((r) => r.data);
};

const scaRecipientStatus = ({ rid }) => {
  return axios.get(`/api/mango/recipients/${rid}/status`).then((r) => r.data);
};

const resumeBankSetup = () => axios.post('/api/mango/recipients/resume');

const fetchBankStatus = () => axios.get('/api/user/bank-status').then((r) => r.data);

const listCottageImages = (roomId) =>
  axios.get(`/api/cottages/${roomId}/images/`).then((res) => res.data);

const fetchCottageImage = (roomId, imageId) =>
  axios.get(`/api/cottages/${roomId}/images/${imageId}`).then((res) => res.data);

const removeCottageImages = (roomId, imageId) =>
  axios.delete(`/api/cottages/${roomId}/images/${imageId}`).then((res) => res.data);

const reorderCottageImages = (roomId, payload) =>
  axios.put(`/api/cottages/${roomId}/images_reorder`, payload).then((res) => res.data);

const updateCottageImage = (roomId, imageId, payload) =>
  axios.put(`/api/cottages/${roomId}/images/${imageId}`, payload).then((res) => res.data);

const createImage = (roomId, payload) =>
  axios
    .post(`/api/cottages/${roomId}/images`, {
      filename: payload.name,
    })
    .then((res) => res.data);

const fetchCalendar = (roomId) =>
  axios.get(`/api/cottages/${roomId}/calendar`).then((res) => res.data);

const updateCalendar = (roomId, payload) =>
  axios.put(`/api/cottages/${roomId}/calendar`, payload).then((res) => res.data);

const updateCottageSettings = (roomId, payload) =>
  axios.put(`/api/cottages/${roomId}`, payload).then((res) => res.data);

const fetchLastMinuteDeals = (cottageId) =>
  axios.get(`/api/cottages/${cottageId}/lastminutes`).then((res) => res.data);

const deleteLastMinuteDeals = (cottageId, lastMinuteId) =>
  axios.delete(`/api/cottages/${cottageId}/lastminutes/${lastMinuteId}`).then((res) => res.data);

const postLastMinuteDeals = (cottageId, payload) =>
  axios.post(`/api/cottages/${cottageId}/lastminutes`, payload).then((res) => res.data);

/**
 * Auth
 */
const postLogin = (data) => axios.post('/api/auth/login', data).then((res) => res.data);

/**
 *
 */
const postLogout = () => axios.get('/api/auth/logout').then((res) => res.data);

/**
 *
 */
const checkUsername = (username) =>
  axios.post('/api/user/checkUsername', { username }).then((res) => res.data);

const sendAuthToken = (username) =>
  axios.post('/api/user/send-auth-token', { username }).then((res) => res.data);

/**
 *
 */
const postCreateUser = (username) => {
  const { lang } = qs.parse(window.location.search);

  return axios
    .post('/api/user?autenticate=yes', {
      username,
      language: lang ? lang.toUpperCase() : undefined,
      // For this call, we need to pass username alone
      // name,
    })
    .then((res) => res.data);
};

/**
 *
 */
const resetUserPassword = (username) =>
  axios.get(`/api/user/resetPassword/${username}`).then((res) => res.data);

const fetchConversations = (limit, offset, tags) =>
  axios
    .get(`/api/conversations?${qs.stringify({ limit, offset, tags: tags.join() })}`)
    .then((res) => res.data);

const fetchConversation = (conversationId) =>
  axios.get(`/api/conversations/${conversationId}`).then((res) => res.data.results);

const fetchConversationCanAskForReview = (conversationId) =>
  axios.get(`/api/conversations/${conversationId}/can-ask-for-review`).then((res) => res.data);

const postAskForReviewMessage = (conversationId, body) =>
  axios
    .post(`/api/conversations/${conversationId}/messages/ask-for-review`, body)
    .then((res) => res.data.results);

const postMessage = (conversationId, text) =>
  axios
    .post(`/api/conversations/${conversationId}/messages`, {
      text,
    })
    .then((res) => res.data.results);

const postHostReview = (conversationId, data) =>
  axios.post(`/api/conversations/${conversationId}/review`, data).then((res) => res.data);

const deleteMessage = (conversationId, messageId) =>
  axios
    .delete(`/api/conversations/${conversationId}/messages/${messageId}`)
    .then((res) => res.data.results);

const postMessageSeen = (conversationId, messageId) =>
  axios
    .post(`/api/conversations/${conversationId}/messages/${messageId}/seen`)
    .then((res) => res.data);

const fetchConversationFile = (conversationId, resourceId) =>
  axios
    .get(`/api/conversations/${conversationId}/files/${resourceId}`)
    .then((res) => res.data.results);

const postInfoConversationFileUpload = (conversationId, publicUrl, fileName) =>
  axios
    .post(`/api/conversations/${conversationId}/files`, {
      path: publicUrl,
      fileName,
    })
    .then((res) => res.data.results);

const postConversationTags = (conversationId, data) =>
  axios.post(`/api/conversations/${conversationId}/tag`, data).then((res) => res.data);

const updateUser = (payload) =>
  axios.put('/api/user', { ...payload }).then((res) => res.data.results);

const changePassword = (body) => axios.put('/api/user/change-password', body).then((r) => r.data);
const requestResetPassword = (body) =>
  axios.post('/api/auth/request-reset-password', body).then((r) => r.data);
const resetPassword = (body) => axios.post('/api/auth/reset-password', body).then((r) => r.data);

const fetchBookings = (conversationId) =>
  axios.get(`/api/conversations/${conversationId}/bookings`).then((res) => res.data);

const fetchBooking = (bookingId) =>
  axios.get(`/api/user/bookings/${bookingId}`).then((res) => res.data);

const fetchCancellationPolicy = () =>
  axios.get('/api/cancellation-policies').then((res) => res.data);

const postUserMangoPayInfo = () => axios.post('/api/mango/user').then((res) => res.data);

const getMangoPayUser = () => axios.get('/api/mango/user').then((res) => res.data);

const manageProxyConsent = () =>
  axios
    .post('/api/mango/manage-consent')
    .then((res) => res.data)
    .catch((error) => {
      const errorMessage = error?.response?.data?.message || error?.message || '';
      if (errorMessage.includes('Consent cannot be given when no proxy is required')) {
        return null;
      }
      throw error;
    });

const postBooking = (payload) => axios.post('/api/bookings', payload).then((res) => res.data);

const putBooking = (id, payload) =>
  axios.put(`/api/bookings/${id}`, payload).then((res) => res.data);

const overlapBooking = (cottageId, body) => {
  return axios.post(`/api/bookings/${cottageId}/overlap`, body).then((res) => res.data);
};

const bookingAction = (
  bookingId,
  action,
  cancellation_reason,
  payment_method,
  card,
  klarna_addressline1,
  klarna_city,
  klarna_postalcode,
) => {
  // action can be 'cancel' or 'payment'
  if (action === 'cancel') {
    return axios
      .put(`/api/bookings/${bookingId}/${action}`, { reason: cancellation_reason })
      .then((res) => res.data);
  }

  if (action === 'payment') {
    return axios
      .post(`/api/bookings/${bookingId}/${action}`, {
        payment_method,
        card,
        klarna_addressline1,
        klarna_city,
        klarna_postalcode,
      })
      .then((res) => res.data);
  }

  return axios.post(`/api/bookings/${bookingId}/${action}`).then((res) => res.data);
};

const fetchUploadedKYC = () => axios.get('/api/mango/kyc').then((res) => res.data);

const uploadIDProof = (payload) => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  return axios.post('/api/mango/kyc/identity', payload, config).then((res) => res.data);
};

const getSignedUrl = (conversationId, file, callback) => {
  const params = {
    fileName: file.name,
  };

  return axios
    .post(`/api/conversations/${conversationId}/upload`, params)
    .then((res) => callback(res.data.results));
};

const postAuthToken = (payload) => axios.post('/api/auth/token', payload).then((res) => res.data);

/**
 * Payment
 */
const fetchPaymentProducts = (cottageId) =>
  axios.get(`/api/cottages/${cottageId}/products`).then((res) => res.data);

const postPaymentOrder = (cottageId, data) =>
  axios.post(`/api/cottages/${cottageId}/orders`, data).then((res) => res.data);

const updatePaymentOrder = (cottageId, orderId, data) =>
  axios.put(`/api/cottages/${cottageId}/orders/${orderId}`, data).then((res) => res.data);

const fetchPaymentOrder = (orderId) =>
  axios.get(`/api/user/orders/${orderId}`).then((res) => res.data);

const fetchOrderStatus = (cottageId, orderId) =>
  axios.get(`/api/cottages/${cottageId}/orders/${orderId}/status`).then((res) => res.data);

const fetchPaymentClientSecret = (cottageId, orderId) =>
  axios.get(`/api/cottages/${cottageId}/orders/${orderId}/pay`).then((res) => res.data);

const postPaymentPay = (cottageId, orderId, data) =>
  axios.post(`/api/cottages/${cottageId}/orders/${orderId}/pay`, data).then((res) => res.data);

const postPaymentInvoice = (cottageId, orderId, data) =>
  axios.post(`/api/cottages/${cottageId}/orders/${orderId}/invoice`, data).then((res) => res.data);

const fetchOrders = (params) => axios.get('/api/user/orders', { params }).then((res) => res.data);

/**
 * DAC 7
 */
const fetchDac7AvailableYears = () =>
  axios.get('/api/dac-seven/available-years').then((res) => res.data);

const fetchDac7Info = (year) => axios.get(`/api/dac-seven/info/${year}`).then((res) => res.data);

/**
 * Reviews
 */
const fetchNextPage = (nextPage) => axios.get(nextPage).then((res) => res.data);

const fetchPublicCottage = (cottageId) =>
  axios.get(`/api/public/cottages/${cottageId}`).then((res) => res.data);

const fetchCottagesTags = () => {
  return axios.get('/api/public/cottages/tags').then((r) => r.data);
};

const fetchReviews = (params) => axios.get('/api/user/reviews', { params }).then((res) => res.data);

const fetchReview = (id) => {
  return axios.get(`/api/user/reviews/${id}`).then((res) => res.data);
};

const updateReview = (id, body) => {
  return axios.put(`/api/user/reviews/${id}`, body).then((res) => res.data);
};

const deleteReview = (id) => {
  return axios.delete(`/api/user/reviews/${id}`).then((res) => res.data);
};

const canSendReview = (cottageId, bookingId) =>
  axios.get(`/api/user/reviews/is-allowed/${cottageId}/${bookingId || ''}`).then((r) => r.data);

const postReview = (data) => axios.post('/api/user/reviews', data).then((res) => res.data);

const postReviewReply = (reviewId, data) =>
  axios.post(`/api/user/reviews/reply/${reviewId}`, data).then((res) => res.data);

const fetchReviewReply = (id) => {
  return axios.get(`/api/user/reviews/reply/${id}`).then((res) => res.data);
};

const updateReviewReply = (id, body) => {
  return axios.put(`/api/user/reviews/reply/${id}`, body).then((res) => res.data);
};

const deleteReviewReply = (id) => {
  return axios.delete(`/api/user/reviews/reply/${id}`).then((res) => res.data);
};

/**
 * Bookings
 */
const fetchBookingsList = (params) =>
  axios.get('/api/user/bookings', { params }).then((res) => res.data);

/**
 * Tags
 */
const fetchTags = () => axios.get('/api/tags').then((res) => res.data);

const postTag = (data) => axios.post('/api/tags', data).then((res) => res.data);

const fetchNewsDetail = (id) => {
  return axios.get(`/api/news/${id}`).then((res) => res.data);
};

/**
 * Feature Flags
 */
export const fetchFlags = () => axios.get('/api/flags').then((r) => r.data);

/**
 * Session Status
 */
const fetchSessionStatus = () => axios.get('/api/user/session-status').then((r) => r.data);

const backToAdmin = () => axios.post('/api/admin/back-to-admin').then((r) => r.data);

export default {
  fetchSelfEvents,
  fetchTodos,
  updateSelfEvents,
  bookingAction,
  checkUsername,
  sendAuthToken,
  createImage,
  deleteLastMinuteDeals,
  deleteMessage,
  fetchBooking,
  fetchBookings,
  fetchCalendar,
  fetchCancellationPolicy,
  fetchConversation,
  fetchConversationFile,
  fetchConversations,
  fetchConversationCanAskForReview,
  postConversationTags,
  fetchCottage,
  fetchCottageImage,
  fetchCottages,
  fetchRentableCottages,
  fetchLastMinuteDeals,
  fetchStats,
  fetchUploadedKYC,
  fetchUserSelf,
  deleteUser,
  getCottageSignedUrl,
  getSignedUrl,
  listCottageImages,
  postAuthToken,
  postBooking,
  putBooking,
  overlapBooking,
  postCottage,
  verifyCottage,
  postCreateUser,
  postInfoConversationFileUpload,
  postLastMinuteDeals,
  fetchBankIdToken,
  bankIdLogin,
  postLogin,
  postLogout,
  postMessage,
  postAskForReviewMessage,
  postHostReview,
  postMessageSeen,
  postUserMangoPayInfo,
  getMangoPayUser,
  manageProxyConsent,
  removeCottage,
  removeCottageImages,
  reorderCottageImages,
  resetUserPassword,
  updateCalendar,
  updateCottage,
  updateCottageImage,
  updateCottageSettings,
  updateUser,
  changePassword,
  requestResetPassword,
  resetPassword,
  updateUserSettings,
  updateUserHostInformation,
  scaEnroll,
  scaRecipientStatus,
  resumeBankSetup,
  fetchBankStatus,
  uploadIDProof,

  fetchPaymentProducts,
  postPaymentOrder,
  updatePaymentOrder,
  fetchPaymentOrder,
  fetchOrderStatus,
  fetchPaymentClientSecret,
  postPaymentPay,
  postPaymentInvoice,
  fetchOrders,

  fetchDac7AvailableYears,
  fetchDac7Info,

  fetchNextPage,
  fetchPublicCottage,
  fetchCottagesTags,
  fetchReviews,
  fetchReview,
  updateReview,
  deleteReview,
  canSendReview,
  postReview,
  postReviewReply,
  fetchReviewReply,
  updateReviewReply,
  deleteReviewReply,
  fetchBookingsList,

  fetchTags,
  postTag,

  fetchNewsDetail,

  fetchFlags,

  fetchSessionStatus,
  backToAdmin,
};
